import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import {
  LuMessageSquare,
  LuPhone,
  LuPlus,
  LuCheck,
  LuClock,
  LuActivity,
} from "react-icons/lu";
import {
  getLeadStatuses,
  changeLeadStage,
  addLeadNote,
  getLeadTimeline,
  getLeadTasks,
  createTask,
  completeTask,
} from "../../Services/engagementService";
import { successToast, errorToast } from "../../utils/toasts";

const waLink = (mobile, text) =>
  `https://wa.me/${String(mobile || "").replace(/\D/g, "")}?text=${encodeURIComponent(text || "")}`;

const typeIcon = {
  status_change: <LuActivity size={14} />,
  note: <LuMessageSquare size={14} />,
  call: <LuPhone size={14} />,
  whatsapp: <LuMessageSquare size={14} />,
  task: <LuClock size={14} />,
  assigned: <LuActivity size={14} />,
};

/**
 * @param {string|number} lprId - the lead_product_relation id
 * @param {string} mobile - lead contact number
 * @param {number} currentStatusId - current lead_status_id
 * @param {string} name - lead name (for prefilled messages)
 */
const LeadEngagementPanel = ({ lprId, mobile, currentStatusId, name }) => {
  const [statuses, setStatuses] = useState([]);
  const [stageId, setStageId] = useState(currentStatusId || null);
  const [timeline, setTimeline] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [note, setNote] = useState("");
  const [taskForm, setTaskForm] = useState({ title: "", type: "call", due_at: "" });
  const [showTask, setShowTask] = useState(false);

  const reload = useCallback(async () => {
    if (!lprId) return;
    try {
      const [tl, tk] = await Promise.all([getLeadTimeline(lprId), getLeadTasks(lprId)]);
      setTimeline(tl || []);
      setTasks(tk || []);
    } catch {
      /* empty until migrated */
    }
  }, [lprId]);

  useEffect(() => {
    getLeadStatuses().then((s) => setStatuses(s || [])).catch(() => {});
  }, []);
  useEffect(() => {
    setStageId(currentStatusId || null);
  }, [currentStatusId]);
  useEffect(() => {
    reload();
  }, [reload]);

  const onStage = async (e) => {
    const id = Number(e.target.value);
    setStageId(id);
    try {
      await changeLeadStage(lprId, id);
      successToast("Stage updated");
      reload();
    } catch (err) {
      errorToast("Could not update stage");
    }
  };

  const onNote = async (kind = "note") => {
    if (!note.trim()) return;
    try {
      await addLeadNote(lprId, note, kind);
      setNote("");
      reload();
    } catch {
      errorToast("Could not save note");
    }
  };

  const onCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title || !taskForm.due_at) return;
    try {
      await createTask({
        lead_id: lprId,
        title: taskForm.title,
        type: taskForm.type,
        due_at: new Date(taskForm.due_at).toISOString(),
      });
      setTaskForm({ title: "", type: "call", due_at: "" });
      setShowTask(false);
      successToast("Follow-up added");
      reload();
    } catch {
      errorToast("Could not add follow-up");
    }
  };

  const onComplete = async (id) => {
    try {
      await completeTask(id);
      reload();
    } catch {
      errorToast("Failed");
    }
  };

  const openTasks = tasks.filter((t) => t.status === "open");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Left: stage + quick actions + tasks */}
      <div className="space-y-5">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <label className="text-xs text-gray-400 uppercase tracking-wide">Lead stage</label>
          <select
            value={stageId || ""}
            onChange={onStage}
            className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium"
          >
            <option value="" disabled>Select stage</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>

          <div className="flex gap-2 mt-4">
            <a
              href={waLink(mobile, `Hi ${name || ""}, `)}
              target="_blank"
              rel="noreferrer"
              onClick={() => addLeadNote(lprId, "Opened WhatsApp", "whatsapp").then(reload).catch(() => {})}
              className="flex-1 flex items-center justify-center gap-2 bg-[#25D366]/10 text-[#128C7E] rounded-lg py-2 text-sm font-medium"
            >
              <LuMessageSquare size={16} /> WhatsApp
            </a>
            <a
              href={`tel:${mobile}`}
              onClick={() => addLeadNote(lprId, "Called lead", "call").then(reload).catch(() => {})}
              className="flex-1 flex items-center justify-center gap-2 bg-[#425F80]/10 text-[#425F80] rounded-lg py-2 text-sm font-medium"
            >
              <LuPhone size={16} /> Call
            </a>
          </div>
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800">Follow-ups</h4>
            <button
              onClick={() => setShowTask((v) => !v)}
              className="flex items-center gap-1 text-sm text-[#425F80]"
            >
              <LuPlus size={15} /> Add
            </button>
          </div>

          {showTask && (
            <form onSubmit={onCreateTask} className="space-y-2 mb-3 bg-gray-50 rounded-lg p-3">
              <input
                placeholder="What to do (e.g. Call about premium)"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
              />
              <div className="flex gap-2">
                <select
                  value={taskForm.type}
                  onChange={(e) => setTaskForm({ ...taskForm, type: e.target.value })}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                >
                  <option value="call">Call</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="meeting">Meeting</option>
                  <option value="followup">Follow-up</option>
                </select>
                <input
                  type="datetime-local"
                  value={taskForm.due_at}
                  onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
                  className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                />
              </div>
              <button className="w-full bg-[#425F80] text-white rounded-lg py-1.5 text-sm">Save</button>
            </form>
          )}

          {openTasks.length === 0 ? (
            <p className="text-sm text-gray-400">No pending follow-ups.</p>
          ) : (
            <div className="space-y-2">
              {openTasks.map((t) => {
                const overdue = new Date(t.due_at) < new Date();
                return (
                  <div key={t.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{t.title}</p>
                      <p className={`text-xs ${overdue ? "text-red-500" : "text-gray-400"}`}>
                        {t.type} · {moment(t.due_at).format("DD MMM, h:mm A")}
                        {overdue ? " · overdue" : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => onComplete(t.id)}
                      className="p-1.5 rounded-full bg-green-50 text-green-600"
                      title="Mark done"
                    >
                      <LuCheck size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right: timeline + note */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col">
        <h4 className="font-semibold text-gray-800 mb-3">Activity</h4>
        <div className="flex gap-2 mb-4">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note…"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
          />
          <button onClick={() => onNote("note")} className="bg-[#425F80] text-white rounded-lg px-4 text-sm">
            Add
          </button>
        </div>
        <div className="flex-1 overflow-y-auto max-h-80 space-y-3">
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-400">No activity yet.</p>
          ) : (
            timeline.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 shrink-0">
                  {typeIcon[a.type] || <LuActivity size={14} />}
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    {a.type === "status_change"
                      ? `Stage changed`
                      : a.note || a.type}
                  </p>
                  <p className="text-xs text-gray-400">{moment(a.created_at).fromNow()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadEngagementPanel;
